import DOMPurify from "dompurify";
import { COUNCIL } from "@/lib/council";
import type { BubbleData } from "@/hooks/use-deliberation";
import { SafeRender } from "@/components/SafeRender";
import { PersonaProvider } from "@/components/qorum/PersonaProvider";

const BY_ID = Object.fromEntries(COUNCIL.map((c) => [c.id, c]));

type Props = { bubble: BubbleData; side?: "left" | "right" };

export function CouncilBubble({ bubble, side = "left" }: Props) {
  const c = BY_ID[bubble.persona];
  if (!c) return null;

  const rawText = (bubble as { text?: unknown }).text;
  const text = typeof rawText === "string" ? rawText : "";
  const isTyping = bubble.state === "typing";
  const isWaiting = bubble.state === "waiting" || bubble.state === "pending";
  const isAbstained = Boolean((bubble as { abstained?: boolean }).abstained);
  if (isWaiting) return null;

  if (isAbstained) {
    return (
      <div
        data-testid={`council-abstention-${c.id}`}
        role="note"
        aria-label={`${c.name} s'est abstenu ce tour`}
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "6px 0",
          opacity: 0.55,
          fontFamily: '"DM Mono", monospace',
          fontSize: 11,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: "var(--smoke)",
        }}
      >
        — {c.name} s'est abstenu·e ce tour —
      </div>
    );
  }


  const Avatar = (
    <div
      aria-hidden
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: c.color,
        color: "var(--ivory)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: '"Playfair Display", serif',
        fontWeight: 700,
        fontSize: 18,
        flexShrink: 0,
        border: "0.5px solid var(--stone-mid)",
        boxShadow: isTyping
          ? `0 0 0 3px var(--ivory), 0 0 0 4px ${c.color}, 0 0 18px 4px ${c.color}aa, 0 0 36px 8px ${c.color}55`
          : "0 0 0 3px var(--ivory), 0 0 0 3.5px var(--stone-mid)",
        animation: isTyping ? "qorumHalo 1.8s ease-in-out infinite" : undefined,
        transition: "box-shadow .4s ease",
      }}
    >
      {c.initial}
    </div>
  );


  const Header = (
    <div
      style={{
        marginBottom: 6,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: side === "right" ? "flex-end" : "flex-start",
      }}
    >
      <div
        style={{
          fontFamily: '"Playfair Display", serif',
          fontStyle: "italic",
          fontSize: 16,
          color: "var(--charcoal)",
          display: "flex",
          gap: 10,
          alignItems: "baseline",
          flexDirection: side === "right" ? "row-reverse" : "row",
        }}
      >
        <strong style={{ fontWeight: 700 }}>{c.name}</strong>
        {c.role && (
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontStyle: "normal",
              fontSize: 10,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: "var(--smoke)",
            }}
          >
            {c.role}
          </span>
        )}
      </div>
      <PersonaProvider
        personaId={c.id}
        logoSize={11}
        fontSize={9}
        align={side === "right" ? "end" : "start"}
      />
    </div>
  );

  const Bubble = (
    <div
      style={{
        background: "var(--cream)",
        border: isTyping ? `0.5px solid ${c.color}` : "0.5px solid var(--stone-mid)",
        padding: "16px 20px",
        maxWidth: 560,
        boxShadow: isTyping
          ? `inset 3px 0 0 ${c.color}, 0 0 24px -8px ${c.color}99`
          : "0 1px 0 rgba(0,0,0,0.03)",
        borderRadius: 2,
        transition: "box-shadow .4s ease, border-color .4s ease",
      }}

    >
      {isTyping ? (
        <span style={{ display: "inline-flex", gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--smoke)",
                animation: `qorumBounce 1.2s ${i * 0.15}s infinite ease-in-out`,
                display: "inline-block",
              }}
            />
          ))}
          <style>{`@keyframes qorumBounce{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-3px);opacity:1}}@keyframes qorumHalo{0%,100%{filter:brightness(1)}50%{filter:brightness(1.18)}}`}</style>
        </span>
      ) : text ? (
        <p
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 14.5,
            lineHeight: 1.6,
            color: "var(--charcoal)",
            margin: 0,
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(text ?? "", {
              ALLOWED_TAGS: ["em", "strong", "p", "br", "a"],
              ALLOWED_ATTR: ["href", "target", "rel"],
            }).replace(
              /<em>(.*?)<\/em>/g,
              `<em style="font-family:'Cormorant Garamond',serif;font-size:16px;color:var(--leather);font-style:italic;">$1</em>`,
            ),
          }}
        />
      ) : (
        <div
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 14.5,
            lineHeight: 1.6,
            color: "var(--charcoal)",
          }}
        >
          <SafeRender value={rawText} asObject="compact" />
        </div>
      )}
    </div>
  );

  return (
    <div
      data-testid={`council-bubble-${c.id}`}
      data-state={bubble.state}
      role="article"
      aria-label={`Contribution de ${c.name}${c.role ? `, ${c.role}` : ""}`}
      aria-busy={isTyping}
      style={{
        display: "flex",
        flexDirection: side === "right" ? "row-reverse" : "row",
        gap: 14,
        alignItems: "flex-start",
      }}
    >
      {Avatar}
      <div style={{ flex: 1, minWidth: 0 }}>
        {Header}
        <div style={{ display: "flex", justifyContent: side === "right" ? "flex-end" : "flex-start" }}>
          {Bubble}
        </div>
      </div>
    </div>
  );
}
